import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

import { ActivityButton, SequenceNumberInput } from "../../../components/Common";
import { ParsedError } from "../../../interfaces";
import { CreateCurriculumRequest } from "../../../interfaces/curriculum";
import { ErrorHandlingTypeEnum, SequenceEnum } from "../../../enums";
import { applyServerFormErrors } from "../../../services";
import {
  useAddCurriculumMutation,
  useGetCurriculumQuery,
  useUpdateCurriculumMutation,
} from "../../../store/api/curriculumApi";
import { useGetEducationSystemSelectListQuery } from "../../../store/api/educationSystemApi";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: () => void;
};

export const AddEditCurriculumModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const { data: systems } = useGetEducationSystemSelectListQuery();
  const { data: item, error: getError } = useGetCurriculumQuery(id as string, {
    skip: !id || !show,
  });
  const [
    addItem,
    { isLoading: isAdding, isError: isErrorAdd, error: errorAdd },
  ] = useAddCurriculumMutation();
  const [
    updateItem,
    { isLoading: isUpdating, isError: isErrorUpdate, error: errorUpdate },
  ] = useUpdateCurriculumMutation();

  const getEmptyValues = (): CreateCurriculumRequest => ({
    name: { en: "", ar: "" },
    code: "",
    educationSystemId: "",
    sequenceNumber: 0,
  });

  const form = useFormik<CreateCurriculumRequest>({
    enableReinitialize: false,
    initialValues: getEmptyValues(),
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(100, t("maxLength", { maxLength: 100, displayName: "englishValue" }))
          .required(t("isNotEmpty", { displayName: "englishValue" })),
        ar: Yup.string()
          .max(100, t("maxLength", { maxLength: 100, displayName: "arabicValue" }))
          .required(t("isNotEmpty", { displayName: "arabicValue" })),
      }),
      code: Yup.string().required(t("isNotEmpty", { displayName: "code" })),
      educationSystemId: Yup.string().required(
        t("isNotEmpty", { displayName: "educationSystem" })
      ),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", { displayName: "sequenceNumber" }),
      ),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          ...values,
          sequenceNumber: Number(values.sequenceNumber),
        };
        if (id) {
          await updateItem({ id, data: payload }).unwrap();
        } else {
          await addItem(payload).unwrap();
        }
        onClose();
      } catch {
        // Form field errors (e.g. duplicate code) are applied in the effect below.
      }
    },
  });

  const onClose = () => {
    form.resetForm({ values: getEmptyValues() });
    onCloseClick();
  };

  useEffect(() => {
    if (!show) {
      form.resetForm({ values: getEmptyValues() });
      return;
    }

    if (id && item) {
      form.setValues({
        name: item.name,
        code: item.code,
        educationSystemId: item.educationSystemId,
        sequenceNumber: item.sequenceNumber ?? 0,
      });
      return;
    }

    if (!id) {
      form.resetForm({ values: getEmptyValues() });
    }
  }, [show, id, item]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isAdding && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isUpdating && isErrorUpdate) _error = errorUpdate as ParsedError;

    if (_error?.type === ErrorHandlingTypeEnum.FORM && _error.error) {
      applyServerFormErrors(form, _error.error);
    }
  }, [isAdding, isUpdating, isErrorAdd, isErrorUpdate]);

  return (
    <Modal id="addEditCurriculum" isOpen={show} toggle={onClose} centered>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
      >
        <ModalHeader className="p-3" toggle={onClose}>
          {id
            ? t("editItem", { item: "curriculumItem" })
            : t("addItem", { item: "curriculumItem" })}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">
              {t("educationSystem")}
              <span className="text-danger">*</span>
            </label>
            <Input
              type="select"
              id="educationSystemId"
              name="educationSystemId"
              value={form.values.educationSystemId}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                !!(form.touched.educationSystemId && form.errors.educationSystemId)
              }
            >
              <option value="">{t("selectEducationSystem")}</option>
              {(systems || []).map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.value?.[langKey] || s.value?.en}
                </option>
              ))}
            </Input>
            {form.touched.educationSystemId && form.errors.educationSystemId ? (
              <FormFeedback type="invalid">
                {form.errors.educationSystemId as string}
              </FormFeedback>
            ) : null}
          </div>
          <SequenceNumberInput
            tabIndex={1}
            value={form.values.sequenceNumber}
            onChange={(e) => {
              e.preventDefault();
              const { value } = e.target;
              const regex =
                /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
              if (regex.test(value.toString())) {
                form.setFieldValue("sequenceNumber", value);
              } else {
                form.setFieldValue("sequenceNumber", "");
              }
            }}
            onBlur={form.handleBlur}
            invalid={
              !!(form.touched.sequenceNumber && form.errors.sequenceNumber)
            }
            sequenceType={SequenceEnum.CURRICULUM}
            onGenerateSequenceNumber={(v: number) => {
              form.setFieldValue("sequenceNumber", v);
            }}
            mode={id ? "edit" : "add"}
            request={{
              type: SequenceEnum.CURRICULUM,
              educationSystemId: form.values.educationSystemId || undefined,
            }}
          />
          {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
            <FormFeedback type="invalid" className="d-block">
              {form.errors.sequenceNumber as string}
            </FormFeedback>
          ) : null}
          <div className="mb-3">
            <label className="form-label">
              {t("code")}
              <span className="text-danger">*</span>
            </label>
            <Input
              id="code"
              name="code"
              value={form.values.code}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.code && form.errors.code)}
            />
            {form.touched.code && form.errors.code ? (
              <FormFeedback type="invalid">
                {form.errors.code as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("nameEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              id="name.en"
              name="name.en"
              value={form.values.name.en}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.name?.en && form.errors.name?.en)}
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("nameAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              id="name.ar"
              name="name.ar"
              value={form.values.name.ar}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.name?.ar && form.errors.name?.ar)}
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link to={"#"} onClick={onClose} className="btn btn-link link-success">
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            type="submit"
            inProgress={isAdding || isUpdating}
            disabled={isAdding || isUpdating || !!getError}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};
